import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OfficeLayout from '../../components/OfficeLayout';
import { createRecord, getDrivers, uploadPDF, allocateDriver, formatApiError } from '../../utils/api';
import { FileUp, Truck, Check } from 'lucide-react';

export default function CreateRecord() {
  const [docType, setDocType] = useState('trip_sheet');
  const [form, setForm] = useState({
    third_party_agent: '', dispatching_hub: '', email_address: '', notes: '',
    trip_sheet_no: '', no_of_waybills: '', no_of_items: '', total_weight: '',
    waybill_no: '', description: '', weight: ''
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getDrivers()
      .then(res => setDrivers(res.data.filter(driver => driver.status === 'active')))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const body = {
        document_type: docType,
        third_party_agent: form.third_party_agent,
        dispatching_hub: form.dispatching_hub,
        email_address: form.email_address,
        notes: form.notes || undefined,
      };
      if (docType === 'trip_sheet') {
        body.trip_sheet_fields = {
          trip_sheet_no: form.trip_sheet_no,
          no_of_waybills: parseInt(form.no_of_waybills),
          no_of_items: parseInt(form.no_of_items),
          total_weight: parseFloat(form.total_weight),
        };
      } else {
        body.waybill_fields = {
          waybill_no: form.waybill_no,
          description: form.description,
          weight: parseFloat(form.weight),
        };
      }
      const { data: rec } = await createRecord(body);
      
      // Upload PDF if provided
      if (pdfFile) {
        try {
          setPdfUploading(true);
          await uploadPDF(rec.id, pdfFile);
          setPdfUploading(false);
        } catch (uploadErr) {
          setPdfUploading(false);
          console.error('PDF upload failed:', uploadErr);
          setError('Record created but PDF upload failed: ' + formatApiError(uploadErr) + '. You can upload the PDF later from the record detail page.');
          setLoading(false);
          return;
        }
      }
      
      if (selectedDriver && pdfFile) await allocateDriver(rec.id, selectedDriver);
      setSuccess('Record created successfully!');
      setTimeout(() => navigate('/active-records'), 1500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent bg-white";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5";

  return (
    <OfficeLayout>
      <div data-testid="create-record-page">
        <h2 className="text-2xl font-bold tracking-tight text-[#1A1A2E] mb-6 text-center">Create New Record</h2>

        <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl mx-auto">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="create-error">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="create-success"><Check size={16} />{success}</div>}

          {/* Document Type */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <label className={labelCls}>Document Type</label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {['trip_sheet', 'waybill'].map(t => (
                <button key={t} type="button" onClick={() => setDocType(t)}
                  className={`p-4 border-2 rounded-lg text-center font-semibold transition-all ${docType === t ? 'border-[#0D9488] bg-blue-50 text-[#0D9488]' : 'border-gray-200 text-[#6B7280] hover:border-gray-300'}`}
                  data-testid={`select-${t}`}>
                  {t === 'trip_sheet' ? 'Trip Sheet' : 'Waybill'}
                </button>
              ))}
            </div>
          </div>

          {/* Common Fields */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-bold text-[#1A1A2E] mb-4">Record Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>3rd Party Agent *</label><input name="third_party_agent" value={form.third_party_agent} onChange={set} className={inputCls} required data-testid="third-party-agent-input" /></div>
              <div><label className={labelCls}>Dispatching Hub *</label><input name="dispatching_hub" value={form.dispatching_hub} onChange={set} className={inputCls} required data-testid="dispatching-hub-input" /></div>
              <div><label className={labelCls}>Email Address *</label><input type="email" name="email_address" value={form.email_address} onChange={set} className={inputCls} required data-testid="email-address-input" /></div>
              <div className="md:col-span-2"><label className={labelCls}>Notes (Optional)</label><textarea name="notes" value={form.notes} onChange={set} rows={2} className={inputCls} data-testid="notes-input" /></div>
            </div>
          </div>

          {/* Type-Specific Fields */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-bold text-[#1A1A2E] mb-4">
              {docType === 'trip_sheet' ? 'Trip Sheet Details' : 'Waybill Details'}
            </h3>
            {docType === 'trip_sheet' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className={labelCls}>Trip Sheet No. *</label><input name="trip_sheet_no" value={form.trip_sheet_no} onChange={set} className={inputCls} required data-testid="trip-sheet-no-input" /></div>
                <div><label className={labelCls}>No. of Waybills *</label><input type="number" name="no_of_waybills" value={form.no_of_waybills} onChange={set} className={inputCls} required min="1" data-testid="no-of-waybills-input" /></div>
                <div><label className={labelCls}>No. of Items *</label><input type="number" name="no_of_items" value={form.no_of_items} onChange={set} className={inputCls} required min="1" data-testid="no-of-items-input" /></div>
                <div><label className={labelCls}>Total Weight (kg) *</label><input type="number" name="total_weight" value={form.total_weight} onChange={set} className={inputCls} required min="0.1" step="0.1" data-testid="total-weight-input" /></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className={labelCls}>Waybill No. *</label><input name="waybill_no" value={form.waybill_no} onChange={set} className={inputCls} required data-testid="waybill-no-input" /></div>
                <div><label className={labelCls}>Weight (kg) *</label><input type="number" name="weight" value={form.weight} onChange={set} className={inputCls} required min="0.1" step="0.1" data-testid="weight-input" /></div>
                <div className="md:col-span-2"><label className={labelCls}>Description *</label><input name="description" value={form.description} onChange={set} className={inputCls} required data-testid="description-input" /></div>
              </div>
            )}
          </div>

          {/* PDF Upload */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-bold text-[#1A1A2E] mb-4 flex items-center gap-2"><FileUp size={16} /> PDF Upload</h3>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer hover:border-[#0D9488] transition-colors">
              <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])} className="hidden" data-testid="pdf-upload-input" />
              {pdfFile ? (
                <div className="text-center"><Check className="mx-auto mb-2 text-green-600" size={24} /><p className="text-sm font-medium text-[#1A1A2E]">{pdfFile.name}</p></div>
              ) : (
                <div className="text-center"><FileUp className="mx-auto mb-2 text-[#6B7280]" size={32} /><p className="text-sm text-[#6B7280]">Click or drag PDF here</p></div>
              )}
            </label>
          </div>

          {/* Driver Allocation */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-bold text-[#1A1A2E] mb-4 flex items-center gap-2"><Truck size={16} /> Driver Allocation</h3>
            <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} className={inputCls} data-testid="driver-select">
              <option value="">— Select a driver (optional) —</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.name}{d.phone ? ` (${d.phone})` : ''}</option>)}
            </select>
            <p className="text-xs text-[#6B7280] mt-2">PDF must be uploaded before allocating. You can also allocate later.</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/dashboard')} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-[#6B7280] hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="px-6 py-2.5 bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors" data-testid="create-record-submit">
              {loading ? (pdfUploading ? 'Uploading PDF...' : 'Creating...') : 'Create Record'}
            </button>
          </div>
        </form>
      </div>
    </OfficeLayout>
  );
}
